// The reschedule policy (src/policies.js): same notice as cancelling — 1 hour
// for group/buddy classes, 20 minutes for individual ones — and 1 reschedule
// per student per month. The LMS doesn't block anything; it warns, and logs
// each move in reschedule_log (SUPABASE_RESCHEDULE_LOG.sql) so it can count.
import { supabase } from './supabaseClient'

export const RESCHEDULES_PER_MONTH = 1

// One student enrolled = individual class (20 minutes); more = group/buddy (1 hour).
export const noticeMinutes = studentCount => (studentCount > 1 ? 60 : 20)

export function classStart(date, time) {
  if (!date) return null
  return new Date(`${date}T${(time || '00:00').slice(0, 5)}:00+05:30`)
}

// Minutes between `at` and the class start (negative once it has started).
export function minutesBefore(date, time, at = new Date()) {
  const start = classStart(date, time)
  return start ? Math.round((start.getTime() - new Date(at).getTime()) / 60000) : null
}

// First moment of the current month, India time, as an ISO string.
export function monthStartISO(now = new Date()) {
  const ym = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7)
  return new Date(`${ym}-01T00:00:00+05:30`).toISOString()
}

// Reschedules each student has used this month: moves logged as asked by the
// student/parent, plus their requests still waiting for approval (an
// approved request is logged when it's approved). `excludeRequestId` leaves
// out the request being looked at. Returns {} if the log table isn't set up.
export async function usedThisMonth(studentIds, { excludeRequestId } = {}) {
  const ids = [...new Set(studentIds.filter(Boolean))]
  const used = Object.fromEntries(ids.map(id => [id, 0]))
  if (!ids.length) return used
  const since = monthStartISO()
  const [{ data: logged }, { data: pending }] = await Promise.all([
    supabase.from('reschedule_log').select('student_id').in('student_id', ids).eq('asked_by', 'student').gte('created_at', since),
    supabase.from('reschedule_requests').select('id, requested_by').in('requested_by', ids).eq('status', 'pending').gte('created_at', since),
  ])
  for (const r of logged || []) used[r.student_id]++
  for (const r of pending || []) if (r.id !== excludeRequestId) used[r.requested_by]++
  return used
}

// Enrolled students of a class: [{ id, full_name }].
export async function classStudents(classId) {
  const { data } = await supabase.from('enrollments').select('student_id, profiles:student_id(full_name)').eq('class_id', classId)
  return (data || []).map(e => ({ id: e.student_id, full_name: e.profiles?.full_name || 'Student' }))
}

// Plain-English warnings for moving `cls`, asked at `askedAt`, for these students.
export function policyWarnings({ cls, studentCount, askedAt = new Date(), students = [], used = {} }) {
  const warnings = []
  const need = noticeMinutes(studentCount)
  const left = minutesBefore(cls.class_date, cls.start_time, askedAt)
  if (left != null && left < need) {
    warnings.push(`Late request: ${left <= 0 ? 'the class had already started' : `only ${left} minutes before the class`}. The policy needs ${need === 60 ? '1 hour' : '20 minutes'} notice, so under the policy this class counts as taken.`)
  }
  for (const s of students) {
    if ((used[s.id] || 0) >= RESCHEDULES_PER_MONTH) {
      warnings.push(`${s.full_name} has already used this month's reschedule (${used[s.id]} so far). Under the policy, another one counts as a class taken.`)
    }
  }
  return warnings
}

// Records a move. Never throws: if the table isn't set up, the reschedule still goes ahead.
export async function logReschedule(rows) {
  if (!rows.length) return
  try { await supabase.from('reschedule_log').insert(rows) } catch (_) {}
}
